import {environment} from "../../../environments/environment";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {HttpHeaders, HttpParams} from "@angular/common/http";
import {KeyValueDto} from "../dto/key-value.dto";
import {UrlDto} from "./url.dto";
import {Additionals} from "./additionals";

export class UrlBuilder {

  private static readonly SLASH: string = '/';

  public static readonly API_HEADER: string = 'Acc-Api';

  private readonly schema: string = environment.api.core.schema;
  private readonly hostname: string = UrlBuilder.getApiHostname();
  private readonly port: number = environment.api.core.port;
  private readonly paths: string[];
  private queryParams: HttpParams;
  private headers: HttpHeaders;
  private readonly params: KeyValueDto[];

  private constructor() {
    this.queryParams = new HttpParams();
    this.headers = new HttpHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    });
    this.params = [];
    this.paths = [];
    // do not use reference of 'environment.api.core.path'
    environment.api.core.path.forEach((p) => this.paths.push(p));
    this.addHttpHeader(UrlBuilder.API_HEADER, 'true');
  }

  public static builder(): UrlBuilder {
    return new UrlBuilder();
  }

  // Je Marke ihr eigener API-Name: Unter fremder Domain waere das Sitzungs-Cookie von core ein Drittanbieter-Cookie
  public static getApiHostname(hostname: string = window.location.hostname): string {
    const brand: RegExpMatchArray | null = hostname.match(/^(?:dash|login)\.(.+)$/);
    return isDefined(brand) ? `core-api.${brand[1]}` : environment.api.core.hostname;
  }

  public build(): UrlDto {
    let url: string = this.getSchema() + '://';
    url += this.getHostname();
    if (this.getPort() > 0) {
      url += ':' + this.getPort();
    }
    this.getPaths().forEach((p) => {
      url = this.appendPath(url, p);
    });
    if (this.getQueryParams().keys().length > 0) {
      url += '?' + this.getQueryParams().toString();
    }
    url = this.replaceParams(url);
    return new UrlDto(new URL(url), this.getHeaders());
  }

  private appendPath(url: string, path: string): string {
    if (url.substring(-1) !== UrlBuilder.SLASH && path.substring(0, 1) !== UrlBuilder.SLASH) {
      url += UrlBuilder.SLASH;
    }
    url += path;
    return url;
  }

  private replaceParams(url: string): string {
    this.getParams().forEach((kv) => {
      const value: string = isDefined(kv.value) ? kv.value : '';
      url = url.replace('{' + kv.key + '}', value);
    });
    return url;
  }

  public addPath(path: string): UrlBuilder {
    if (nonBlank(path)) {
      this.paths.push(path);
    }
    return this;
  }

  public addQueryParams(param: string, value: string | number | boolean | undefined): UrlBuilder {
    if (isDefined(value)) {
      this.queryParams = this.queryParams.append(param, value);
    }
    return this;
  }

  public addHttpHeader(name: string, value: string | string[]): UrlBuilder {
    this.headers = this.headers.append(name, value);
    return this;
  }

  public setHttpHeader(name: string, value: string | string[]): UrlBuilder {
    this.headers = this.headers.set(name, value);
    return this;
  }

  public deleteHttpHeader(name: string): UrlBuilder {
    this.headers = this.headers.delete(name);
    return this;
  }

  /**
   * Special case, with MultipartFormData the content type is set to empty
   */
  public setContentTypeMultipartFormData(): UrlBuilder {
    return this.deleteHttpHeader('Content-Type');
  }

  public addParam(key: string, value: string | null | undefined): UrlBuilder {
    if (isDefined(value) || value === null) {
      this.addKv(KeyValueDto.of(key, value));
    }
    return this;
  }

  public addKv(param: KeyValueDto): UrlBuilder {
    this.params.push(param);
    return this;
  }

  public addAdditionals(add: Additionals | undefined): UrlBuilder {
    add?.asArray().forEach((name) => this.addQueryParams(name, true));
    return this;
  }

  public getSchema(): string {
    return this.schema;
  }

  public getHostname(): string {
    return this.hostname;
  }

  public getPort(): number {
    return this.port;
  }

  public getPaths(): string[] {
    return this.paths;
  }

  public getQueryParams(): HttpParams {
    return this.queryParams;
  }

  public getHeaders(): HttpHeaders {
    return this.headers;
  }

  public getParams(): KeyValueDto[] {
    return this.params;
  }
}
